import {
  faArchive,
  faBan,
  faBoxArchive,
  faClone,
  faEdit,
  faPlus,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Stack,
  Table,
} from "react-bootstrap";
import ArchiveButton from "../../Helpers/archiveButton";
import DeleteButton from "../../Helpers/deleteButton";
import { PolicyType } from "../../models/PolicyType";
import PolicyTypeService from "../../services/policyType.service";
import { ModalPolicyTypeForm } from "./ModalPolicyTypeForm";
import "./PolicyType.style.css";
import ModalArchivedPolicyTypes from "./ModalArchivedPolicyTypes";

type Notification = {
  show: boolean;
  message: string;
  title: string;
  variant: "success" | "danger";
};

export default function PolicyTypesManager() {
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [modalShow, setModalShow] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [policyToCloneId, setPolicyToCloneId] = useState<number | null>(null);
  const [newPolicyName, setNewPolicyName] = useState("");
  const [cloneError, setCloneError] = useState("");
  const [notification, setNotification] = useState<Notification | null>(null);
  const [filterText, setFilterText] = useState("");
  const [selectedPolicyType, setSelectedPolicyType] =
    useState<PolicyType | null>(null);

  function onPolicyTypeStatusChange(isActive: boolean, policyTypeID: number) {
    const myNewList = [...policyTypes];
    const current = myNewList.find((a) => a.id === policyTypeID);

    if (current) {
      current.isActive = isActive;
      setPolicyTypes(myNewList);
      PolicyTypeService.update(current);
    }
  }

  async function onPolicyTypeRemove(policyType: PolicyType) {
    var response = await PolicyTypeService.remove(policyType);
    if (response.ok) {
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: "Tipo de auditoría eliminada correctamente.",
      });
      await loadPolicyTypes();
    } else {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: await response.text(),
      });
    }
  }

  const handleCloneClick = (idToClone: number) => {
    setPolicyToCloneId(idToClone);
    setNewPolicyName("");
    setCloneError("");
    setShowCloneModal(true);
  };

  const handleCloseCloneModal = () => {
    setShowCloneModal(false);
    setPolicyToCloneId(null);
    setNewPolicyName("");
    setCloneError("");
  };

  const handleCloneSave = async () => {
    if (!newPolicyName.trim()) {
      setCloneError(
        "El nombre del nuevo tipo de auditoría no puede estar vacío."
      );
      return;
    }

    if (policyToCloneId === null) {
      setCloneError("No se ha seleccionado un tipo de auditoría para clonar.");
      return;
    }

    try {
      const request = {
        auditTypeIdToClone: policyToCloneId,
        newAuditTypeName: newPolicyName,
      };

      // Asumiendo que tienes un método clone en PolicyTypeService
      const successMessage = await PolicyTypeService.clone(request);
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: successMessage ? successMessage : "Clonado",
      });

      // Actualizar la lista
      await loadPolicyTypes();
      handleCloseCloneModal();
    } catch (error: any) {
      console.error("Error cloning policy type:", error.message);
      setCloneError(`Error al clonar: ${error.message}`);
    }
  };

  const handleSave = async (policyType: PolicyType) => {
    try {
      if (policyType.id) {
        const response = await PolicyTypeService.update(policyType);
        if (response.ok) {
          setNotification({
            show: true,
            variant: "success",
            title: "Éxito",
            message: "Tipo de auditoría actualizada correctamente.",
          });
          setSelectedPolicyType(null);
          setModalShow(false);
          await loadPolicyTypes();
          return;
        } else {
          const errorMessage = await response.text();
          throw new Error(
            errorMessage || "Error al actualizar tipo de auditoría."
          );
        }
      } else {
        const response = await PolicyTypeService.insert(policyType);

        if (response.ok) {
          setNotification({
            show: true,
            variant: "success",
            title: "Éxito",
            message: "Tipo de auditoría agregada correctamente.",
          });
          setSelectedPolicyType(null);
          setModalShow(false);
          await loadPolicyTypes();
          return;
        } else {
          const errorMessage = await response.text();
          throw new Error(
            errorMessage || "Error al agregar tipo de auditoría."
          );
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: error instanceof Error ? error.message : "",
      });
    }
  };

  const handleArchive = async (policyType: PolicyType) => {
    const response = await PolicyTypeService.archive(policyType);

    if (response.ok) {
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: policyType.isArchived
          ? "Tipo de auditoría restaurada correctamente."
          : "Tipo de auditoría archivada correctamente.",
      });
      await loadPolicyTypes();
      return;
    } else {
      const errorMessage = await response.text();
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: errorMessage || "Error al archivar tipo de auditoría.",
      });
    }
  };

  async function loadPolicyTypes() {
    try {
      const data = await PolicyTypeService.getPoliciTypes();
      setPolicyTypes(data);
    } catch (error: any) {
      console.error("Error loading policy types:", error.message);
    }
  }

  const getFilteredPolicyTypes = () => {
    const search = filterText.toLowerCase();

    return policyTypes.filter((p) => {
      return (
        p.isArchived === false &&
        (p.policyDesc?.toLowerCase().includes(search) ||
          p.id?.toString().includes(search))
      );
    });
  };

  useEffect(() => {
    loadPolicyTypes();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <>
      <div className="notification-container">
        {notification && (
          <Alert
            variant={notification.variant}
            onClose={() => setNotification(null)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{notification.title}</Alert.Heading>
            {notification.message}
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <Card>
          <Card.Body>
            <Card.Title>Tipos de auditoría</Card.Title>
            <hr />
            <Row className="mb-3 g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Col>
              <Col
                md={2}
                className="d-flex justify-content-md-start align-items-center"
              >
                <Stack direction="horizontal" gap={3}>
                  <Button
                    onClick={() => {
                      setShowArchivedModal(true);
                    }}
                    variant="primary"
                    className="d-flex align-items-center"
                  >
                    <FontAwesomeIcon icon={faArchive} className="white" />
                    <span className="ms-2">Archivados</span>
                  </Button>
                </Stack>
              </Col>
              <Col
                md={5}
                className="d-flex justify-content-md-end align-items-center"
              >
                <Stack direction="horizontal" gap={3}>
                  <Button
                    onClick={() => {
                      setSelectedPolicyType(null);
                      setModalShow(true);
                    }}
                    variant="primary"
                    className="d-flex  align-items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="white" />
                    <span className="ms-2">Nuevo tipo</span>
                  </Button>
                </Stack>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table striped bordered hover>
                  <thead>
                    <tr className="text-center">
                      <th>Id</th>
                      <th>Descripción</th>
                      <th>Activo</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredPolicyTypes().map((pt) => (
                      <tr key={pt.id}>
                        <td className="text-center">{pt.id}</td>
                        <td>{pt.policyDesc}</td>
                        <td className="text-center">
                          <Form.Check
                            type="switch"
                            checked={pt.isActive}
                            onChange={(e) => {
                              onPolicyTypeStatusChange(
                                e.target.checked,
                                pt.id!
                              );
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <Stack
                            direction="horizontal"
                            gap={2}
                            className="justify-content-center"
                          >
                            <Button
                              variant="primary"
                              size="sm"
                              title="Editar"
                              onClick={() => {
                                setSelectedPolicyType(pt);
                                setModalShow(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="white"
                              />
                            </Button>
                            <Button
                              variant="secondary"
                              active
                              size="sm"
                              title="Clonar"
                              onClick={() => handleCloneClick(pt.id!)}
                            >
                              <FontAwesomeIcon
                                icon={faClone}
                                className="white"
                              />
                            </Button>
                            <ArchiveButton
                              title="Archivar"
                              description="¿Estás seguro que deseas archivar el registro?"
                              iconButton={faBoxArchive}
                              onClick={() => {
                                handleArchive(pt);
                              }}
                            />
                            <DeleteButton
                              onClick={() => {
                                onPolicyTypeRemove(pt);
                              }}
                            />
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
      <ModalPolicyTypeForm
        show={modalShow}
        onHide={() => setModalShow(false)}
        onSave={handleSave}
        policyType={selectedPolicyType}
        title={
          selectedPolicyType
            ? "Editar tipo de auditoría"
            : "Nuevo tipo de auditoría"
        }
      />

      {/* Modal de Archivados */}
      <ModalArchivedPolicyTypes
        onHide={() => setShowArchivedModal(false)}
        show={showArchivedModal}
        policyType={policyTypes}
        onRestore={handleArchive}
      />

      {/* Modal de Clonación */}
      <Modal
        show={showCloneModal}
        onHide={handleCloseCloneModal}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Clonar tipo de auditoría</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cloneError && <p className="text-danger">{cloneError}</p>}
          <Form.Group className="mb-3">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              value={newPolicyName}
              onChange={(e) => setNewPolicyName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCloneModal}>
            <FontAwesomeIcon
              icon={faBan}
              style={{
                paddingRight: "3px",
              }}
            />
            <span className="ms-2">Cancelar</span>
          </Button>
          <Button variant="primary" onClick={handleCloneSave}>
            <FontAwesomeIcon
              icon={faSave}
              style={{
                paddingRight: "3px",
              }}
            />
            <span className="ms-2">Guardar</span>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
